import { Box, FormControl, FormLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useMemo } from 'react';

import { DashboardI } from '../../declarations/reporting';
import { SelectsWrapperStyled } from './SelectCategories.styled';
import DotsLoader from 'components/DotsLoader/DotsLoader';
export interface DashboardCategoryReportSelectProps {
  dashboards: DashboardI[] | undefined;
  value: DashboardI;
  onChange: (dashboard: DashboardI) => void;
  isLoading: boolean;
  categoryValue?: string | null;
  onCategoryChange?: (category: string | null) => void;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

function splitName(name: string): { category: string; report: string } {
  const idx = name.indexOf(' - ');
  if (idx === -1) return { category: 'Other', report: name };
  return { category: name.slice(0, idx), report: name.slice(idx + 3) };
}

function findById(list: DashboardI[] | undefined, id: string | null | undefined) {
  if (!id) return undefined;
  return list?.find(d => d?.dashboardId === id || undefined);
}

const SelectCategoryReport: React.FC<DashboardCategoryReportSelectProps> = ({
  dashboards,
  value,
  onChange,
  categoryValue,
  onCategoryChange,
  fullWidth = true,
  size = 'small',
  disabled = false,
  isLoading,
}) => {
  // Build category -> dashboards map
  const { categories, byCategory } = React.useMemo(() => {
    const map = new Map<string, DashboardI[]>();
    const order: string[] = [];
    dashboards?.forEach(d => {
      const { category } = splitName(d.dashboardName);
      if (!map.has(category)) {
        map.set(category, []);
        order.push(category);
      }
      map.get(category)!.push(d);
    });
    return { categories: order, byCategory: map };
  }, [dashboards]);

  // Derive category from current selected dashboard
  const selectedItem = useMemo(
    () => findById(dashboards, value?.dashboardId ?? null),
    [dashboards, value]
  );
  const derivedCategoryFromValue = selectedItem
    ? splitName(selectedItem.dashboardName).category
    : null;

  // Category controlled/uncontrolled
  const isCategoryControlled = categoryValue !== undefined;
  const [internalCategory, setInternalCategory] = React.useState<string>('');
  const selectedCategory =
    (isCategoryControlled ? categoryValue : internalCategory) || derivedCategoryFromValue || '';

  React.useEffect(() => {
    if (isCategoryControlled) return;
    setInternalCategory(derivedCategoryFromValue || '');
  }, [isCategoryControlled, derivedCategoryFromValue]);

  // If the currently selected report doesn't belong to the selected category, blank the report Select value
  const selectedBelongsToCategory = React.useMemo(() => {
    if (!selectedItem || !selectedCategory) return false;
    return splitName(selectedItem.dashboardName).category === selectedCategory;
  }, [selectedItem, selectedCategory]);

  const selectedReportId = selectedBelongsToCategory ? selectedItem?.dashboardId || '' : '';

  const handleCategoryChange = (e: SelectChangeEvent<string>) => {
    const nextCategory = e.target.value || '';
    if (!isCategoryControlled) setInternalCategory(nextCategory);
    onCategoryChange?.(nextCategory || null);
    // NOTE: Do not call onChange here; parent keeps current dashboard until a report is picked.
  };

  const handleReportChange = (e: SelectChangeEvent<string>) => {
    const nextId = e.target.value || '';
    const item = findById(dashboards, nextId || null);
    if (item) onChange(item);
  };

  const reports = selectedCategory ? byCategory.get(selectedCategory) || [] : [];

  return (
    <SelectsWrapperStyled>
      <FormControl fullWidth={fullWidth} size={size} disabled={disabled}>
        <FormLabel id="dash-cat-label" sx={{ mb: 0.5 }}>
          Select Category Report
        </FormLabel>
        <Select
          labelId="dash-cat-label"
          value={selectedCategory}
          onChange={handleCategoryChange}
          displayEmpty
          renderValue={val =>
            isLoading ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <DotsLoader />
              </Box>
            ) : val ? (
              String(val)
            ) : (
              <span style={{ opacity: 0.6 }}>Select Category Report</span>
            )
          }
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {categories.map(cat => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        fullWidth={fullWidth}
        size={size}
        disabled={disabled || isLoading || !selectedCategory}
      >
        <FormLabel id="dash-report-label" sx={{ mb: 0.5 }}>
          Select Report
        </FormLabel>
        <Select
          labelId="dash-report-label"
          value={selectedReportId}
          onChange={handleReportChange}
          displayEmpty
          renderValue={val => {
            if (!val) return <span style={{ opacity: 0.6 }}>Select Report</span>;
            const item = findById(dashboards, String(val));
            return item ? splitName(item.dashboardName).report : 'Select Report';
          }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {reports.map(item => (
            <MenuItem key={item.dashboardId} value={item.dashboardId}>
              {splitName(item.dashboardName).report}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </SelectsWrapperStyled>
  );
};

export default SelectCategoryReport;
